import {
  HeaderType,
  HeaderObject,
} from '@estee/elc-universal-utils';

export type RequestOptions = {
  cache?: RequestCache;
  credentials?: RequestCredentials;
};

export interface IResponse<T> {
  data: T | null;
  reponseHeaders: Headers;
}

export interface IError {
  message: string;
  status: number;
}

export const textContentTypes = [
  'text/css',
  'text/csv',
  'text/html',
  'text/event-stream',
  'text/javascript',
  'text/plain',
  'text/xml',
];

export class ApiError extends Error implements IError {
  public status: number;

  public errorResponse: object;

  constructor(httpStatus: number, errorResponse: object) {
    super(`${httpStatus}`);
    this.status = httpStatus;
    this.errorResponse = errorResponse;
  }
}

export class HttpTransport {
  private baseUrl: string;

  private globalHeaders: HeaderObject = {};

  public setBaseUrl = (baseUrl: string): void => {
    if (!this.baseUrl) {
      this.baseUrl = baseUrl;
    }
  };

  public addGlobalHeaders = (headers: HeaderObject): void => {
    this.globalHeaders = { ...this.globalHeaders, ...headers };
  };

  public removeGlobalHeader = (headerName: HeaderType): void => {
    delete this.globalHeaders[headerName];
  };

  protected getRequestHeaders = async (headers?: Record<string, string>): Promise<{ headers: Headers; }> => {
    const httpHeader = new Headers({
      ...this.globalHeaders,
      ...headers,
    });

    return { headers: httpHeader };
  };

  public makeRequest = async <T>(
    url: string,
    method: string,
    body?: {},
    options?: RequestInit & { signal?: AbortSignal },
    headers?: {},
  ): Promise<IResponse<T>> => {
    let formattedBody = body;
    const requestHeaders = await this.getRequestHeaders(headers);
    if (requestHeaders.headers.get('content-type') === 'application/json' && body) {
      formattedBody = JSON.stringify(body);
    }

    const config: { url: string } & RequestInit = {
      method: method.toUpperCase(),
      mode: 'cors',
      url: this.getUrl(url),
      ...options,
      ...requestHeaders,
      body: formattedBody as BodyInit,
    };
    const { url: reqUrl, ...init } = config;

    const fetchResponse = await fetch(reqUrl, init);
    const data = await this.dataFetchHandler(fetchResponse);

    const response: IResponse<T> = {
      data,
      reponseHeaders: fetchResponse.headers,
    };

    if (fetchResponse.status >= 300) {
      throw new ApiError(fetchResponse.status, response);
    }
    return response;
  };

  private getUrl = (url: string): string => {
    if (!this.baseUrl || url.includes(this.baseUrl)) {
      return url;
    }
    if (url.indexOf('http') === 0) {
      return url;
    }

    return this.baseUrl + url;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private dataFetchHandler = (response?: Response): Promise<any> => {
    const contentType = response.headers.get('content-type')?.toLowerCase() || '';

    return HttpTransport.hasContentType(contentType, textContentTypes) ||
      this.isBodyResponseEmpty(response) ||
      (response.status > 500 && contentType !== 'application/json') ? response.text() : response.json();
  };

  private isBodyResponseEmpty = (response: Response): boolean => {
    if (response.status === 204) {
      return true;
    }

    if (
      response.headers.has('content-length') &&
        Number(response.headers.get('content-length')) === 0
    ) {
      return true;
    }
  };

  static hasContentType(text: string, listToMatchAgainst: string[]): boolean {
    const values = listToMatchAgainst.join('|');

    return !!text.match(new RegExp(`\\b(?:${values})\\b`, 'gi'));
  }
}
